/**
 * Start a run from the Tests page and go watch it.
 *
 * Three callers want exactly this: **New Test**, which starts a run that
 * writes tests, the per-test buttons, which start a run scoped to one
 * test, and **Run Suite**, which hands over what the replay settings
 * dialog collected. They differ in which settings they use and what they
 * send, not in what happens around it — trigger, say so, go to the run.
 *
 * The Runs page deliberately does NOT use this. It owns a live run with a
 * socket attached and shows it in place, so "go somewhere" is the one
 * thing it must not do. That exclusion is about what the page is, not
 * about how many callers there happen to be.
 *
 * Nothing here saves: every caller passes its values as a one-run
 * override, and the settings they borrow are left as they were.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { triggerRun } from '@/lib/api'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'

export interface StartRun {
  /** Trigger, then open the run's report. `settingsOverride` applies to
   * this run only. Resolves when the request has settled. */
  start: (templateId: string, startedMessage: string, settingsOverride?: Record<string, unknown>) => Promise<void>
  /** True while a request is in flight. Disables the trigger. */
  starting: boolean
}

export function useStartRun(): StartRun {
  const navigate = useNavigate()
  const wp = useWorkspacePath()
  const toast = useToast()
  const [starting, setStarting] = useState(false)

  async function start(
    templateId: string,
    startedMessage: string,
    settingsOverride?: Record<string, unknown>,
  ): Promise<void> {
    if (starting) return
    setStarting(true)
    try {
      const { runId } = await triggerRun(
        templateId,
        settingsOverride ? { settingsOverride } : {},
      )
      toast({ title: startedMessage, variant: 'success' })
      // Watching it is why the button was pressed (#2194). A link the
      // operator has to notice is a step between them and what they asked
      // for, and the one they could not find on screen at all.
      //
      // The Runs page, not `/runs/<id>/report` (#2206): that route is the
      // Playwright HTML report, which exists only once a run has finished,
      // so it opened on "Report not found" every time. The run id rides
      // along so the page attaches to THIS run rather than to whichever
      // one the active list returns first.
      navigate(wp(`/results?run=${runId}`))
    } catch (err) {
      toast({
        title: 'Could not start',
        description: toUserMessage(err, "Couldn't start the run. Try again."),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
    } finally {
      setStarting(false)
    }
  }

  return { start, starting }
}
